import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Container, Row, Col, Card, Button, Form, Spinner, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faFileInvoice,
  faDownload,
  faQuestionCircle,
  faTimes,
  faSync,
  faUpload,
  faEdit
} from "@fortawesome/free-solid-svg-icons";

import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { useHistory } from "react-router-dom";
import config from "../../config/config.json";
import { apiCall } from "../../_services/apiCall";
import { getStore } from "../../utils/UniversalFunction";
import DocumentForm from "./DocumentForm";
import { 
  getStartingMonth, 
  getFrequency, 
  getPeriod,
  getPeriodValue,
  generateTimePeriodOptions, 
  handlePeriodChange 
} from "../../utils/PeriodCalculationUtils";
import "./DocumentListingComponent.css";
import { from } from "rxjs";

const DocumentListingComponent = ({
  documents,
  sources,
  financialYears,
  financialYearId,
  setFinancialYearId,
  loading: parentLoading = false,
  frameworks,
  onDocumentUpdate, // Callback to refresh the document list
  onFiltersChange, // Callback to apply filters
}) => {

  const history = useHistory();

  // Memoized options for DocumentForm
  const sourceOptions = sources.map(s => ({
    value: s.id,
    label: s.unitCode || s.location.area
  }));

  const financialYearOptions = financialYears
    .map(fy => ({
      value: fy.id,
      label: fy.financial_year_value
    }));

  const moduleOptions = [...new Set(documents.map(doc => doc.moduleName))].map(moduleName => ({ value: moduleName, label: moduleName}));  

  const documentTypeToSubTypes = documents.reduce((acc, doc) => {
    const docType = doc.documentType;
    const subType = doc.documentMetadata?.documentSubType;

    if (!acc[docType]) {
      acc[docType] = new Set(); // use Set to avoid duplicates
    }

    if (!subType) return acc; // skip if missing

    acc[docType].add(subType);

    return acc;
  }, {});

  // Convert sets to arrays
  const documentTypeToSubTypesMap = Object.fromEntries(
    Object.entries(documentTypeToSubTypes).map(([docType, subTypes]) => [docType, [...subTypes]])
  );

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter state
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedFrequency, setSelectedFrequency] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [selectedDocumentSubType, setSelectedDocumentSubType] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');

  // Time period state
  const [answerFrequency, setAnswerFrequency] = useState();
  const [timePeriodOptions, setTimePeriodOptions] = useState([]);
  const [disablePeriodSelection, setDisablePeriodSelection] = useState(true);

  const financialYearStartMonth = useMemo(() => getStartingMonth(), []);

  // Document filtering and processing functions
  const filterDocuments = useCallback((documents, filters) => {
    if (!documents || !Array.isArray(documents)) {
      return [];
    }

    const {
      financialYearId,
      moduleName,
      locationId,
      frequency,
      fromDate,
      toDate,
      documentType,
      documentSubType,
    } = filters;

    return documents.filter(document => {
      // Financial Year Filter
      if (financialYearId && document.financialYearId !== parseInt(financialYearId)) {
        return false;
      }

      // Financial Year Filter
      if (selectedModule && document.moduleName !== moduleName) {
        return false;
      }

      // Location Filter (sourceId)
      if (locationId && document.sourceId !== parseInt(locationId)) {
        return false;
      }

      // Document Type Filter
      if (documentType && document.documentType !== documentType) {
        return false;
      }

      // Document Sub Type Filter
      if (documentSubType && document.documentMetadata?.documentSubType !== documentSubType) {
        return false;
      }

      // Frequency Filter
      if (frequency && document.frequency !== frequency) {
        return false;
      }

      // Date Range Filter (when period/dates are selected)
      if (fromDate && toDate) {
        return document.fromDate === fromDate && document.toDate === toDate;
      }

      return true;
    });
  }, []);

  // Filtered and processed documents
  const filteredDocuments = useMemo(() => {
    let processedDocuments = documents;

    // Apply filters
    const filters = {
      financialYearId,
      locationId: selectedLocation,
      frequency: selectedFrequency,
      moduleName: selectedModule,
      documentType: selectedDocumentType,
      documentSubType: selectedDocumentSubType,
      fromDate: filterFromDate,
      toDate: filterToDate,
    };

    processedDocuments = filterDocuments(processedDocuments, filters);

    return processedDocuments;
  }, [
    documents, 
    financialYearId, 
    selectedModule, 
    selectedDocumentType, 
    selectedDocumentSubType, 
    selectedLocation, 
    selectedFrequency, 
    filterFromDate, 
    filterToDate, 
    filterDocuments
  ]);

  // Modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editDocument, setEditDocument] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editSuccess, setEditSuccess] = useState(false);

  const currentUser = getStore("currentUser");

  // Date setters for period calculation
  const setFromDateFilter = useCallback((fromDate) => {
    setFilterFromDate(fromDate);
  }, []);

  const setToDateFilter = useCallback((toDate) => {
    setFilterToDate(toDate);
  }, []);

  // Handle financial year selection
  const handleFinancialYearChange = useCallback((selectedOption) => {
    setFinancialYearId(selectedOption);
    
    // Reset all other filters when financial year changes
    resetOtherFilters();
  }, [setFinancialYearId]);

  // Reset other filters (not financial year)
  const resetOtherFilters = useCallback(() => {
    setSelectedModule('');
    setSelectedDocumentType('');
    setSelectedDocumentSubType('');
    setSelectedLocation('');
    setSelectedFrequency('');
    setSelectedPeriod('');
    setFilterFromDate('');
    setFilterToDate('');
    setTimePeriodOptions([]);
    setDisablePeriodSelection(true);
  }, []);

  // Handle frequency change
  const handleFrequencyChange = useCallback((frequency) => {
    setSelectedFrequency(frequency);
    
    if (frequency === 'CUSTOM') {
      setDisablePeriodSelection(false);
    } else {
      setDisablePeriodSelection(true);
      setSelectedPeriod('');
    }
  }, []);

  // Handle period change
  const handlePeriodChangeFilter = useCallback((period) => {
    setSelectedPeriod(period);
    
    if (financialYearId && financialYearOptions) {
      const frequency = selectedFrequency === 'CUSTOM' ? answerFrequency : 'YEARLY';
      handlePeriodChange(period, financialYearId, financialYearOptions, frequency, setFromDateFilter, setToDateFilter);
    }
  }, [financialYearId, financialYearOptions, selectedFrequency, answerFrequency, setFromDateFilter, setToDateFilter]);

  // Fetch frequency data when financial year changes
  useEffect(() => {
    const fetchFrequency = async () => {
      if (financialYearId) {
        try {
          const frequency = await getFrequency(financialYearId);
          setAnswerFrequency(frequency);
        } catch (err) {
          console.error("Error fetching frequency", err);
        }
      }
    };

    fetchFrequency();
  }, [financialYearId]);

  // Generate time period options when frequency or financial year changes
  useEffect(() => {
    if (!selectedFrequency || !financialYearId || !financialYearStartMonth) return;

    if (selectedFrequency === 'CUSTOM' && answerFrequency) {
      const timePeriodOpts = generateTimePeriodOptions(answerFrequency, financialYearStartMonth);
      setTimePeriodOptions(timePeriodOpts);
    } else if (selectedFrequency !== 'CUSTOM') {
      const timePeriodOpts = generateTimePeriodOptions('YEARLY', financialYearStartMonth);
      setTimePeriodOptions(timePeriodOpts);
    }
  }, [selectedFrequency, answerFrequency, financialYearId, financialYearStartMonth]);

  // Apply filters
  const applyFilters = useCallback(() => {
    const filters = {
      financialYearId,
      locationId: selectedLocation,
      frequency: selectedFrequency,
      period: selectedPeriod,
      fromDate: filterFromDate,
      toDate: filterToDate,
    };

    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [financialYearId, selectedLocation, selectedFrequency, selectedPeriod, filterFromDate, filterToDate, onFiltersChange]);

  // Reset all filters (excluding financial year)
  const resetFilters = useCallback(() => {
    resetOtherFilters();
    
    // Apply filters with reset values
    const filters = {
      financialYearId,
      moduleName: '',
      documentType: '',
      documentSubType: '',
      locationId: '',
      frequency: '',
      period: '',
      fromDate: '',
      toDate: '',
    };

    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [financialYearId, resetOtherFilters, onFiltersChange]);

  // Open document preview modal
  const openPreviewModal = (document) => {
    setShowPreviewModal(true);
    setPreviewDocument(document);
  };

  // Close document preview modal
  const closePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewDocument(null);
  };

  // Open edit modal
  const openEditModal = async (document) => {
    setEditLoading(true);
    setEditError(null);
    try {
      // Fetch the complete document data for editing
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}documents/${document.id}`,
        {},
        {},
        "GET"
      );

      if (isSuccess && data) {
        setEditDocument(data);
        setShowEditModal(true);
      } else {
        setEditError("Failed to load document details for editing.");
      }
    } catch (error) {
      console.error("Error fetching document for edit:", error);
      setEditError("An error occurred while loading the document for editing.");
    } finally {
      setEditLoading(false);
    }
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditDocument(null);
    setEditError(null);
    setEditSuccess(false);
  };

  // Handle document save from edit form
  const handleDocumentSave = (savedData, updatedData) => {
    setEditSuccess(true);
    setEditError(null);
    
    // Update the document in the edit modal
    setEditDocument(prev => ({
      ...prev,
      ...updatedData
    }));

    // Call parent callback to refresh the document list
    if (onDocumentUpdate) {
      onDocumentUpdate();
    }

    // Close modal after a short delay to show success message
    setTimeout(() => {
      closeEditModal();
    }, 2000);
  };

  // Navigate to reporting modules with document ID
  const navigateToReportingModule = (documentId) => {
    history.push(`/reporting-modules?documentId=${documentId}`);
  };

  const navigateToUpload = () => {
    history.push('/upload-document');
  };

  return (
    <div className="document-listing-container">
      {/* Always visible section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title mb-0">Documents</h2>
        
        <Button
          variant="primary"
          onClick={navigateToUpload}
        >
          <FontAwesomeIcon icon={faUpload} className="me-2" />
          Upload New Document
        </Button>
      </div>

      <Card className="filter-card mb-4">
        <Card.Body>
          <Row>
            {/* Filter and Reset Buttons */}
            <Col md={3} className="mb-3 d-flex">
              <Button variant="primary" className="me-2 flex-grow-1" onClick={applyFilters}>
                <FontAwesomeIcon icon={faFilter} className="me-1" />
                Apply Filters
              </Button>
              <Button variant="outline-secondary" className="flex-grow-1" onClick={resetFilters}>
                <FontAwesomeIcon icon={faTimes} className="me-1" />
                Reset
              </Button>
            </Col>
          </Row>

          <Row>
            {/* Financial Year Filter */}
            <Col md={3} className="mb-3">
              <Form.Group className="mb-3">
                <Form.Label>Financial Year</Form.Label>
                <Form.Select
                  value={financialYearId}
                  onChange={(e) => handleFinancialYearChange(e.target.value)}
                >
                  {financialYearOptions && (financialYearOptions.map((fy) => (
                    <option key={fy.value} value={fy.value}>
                      {fy.label}
                    </option>
                  )))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Module Filter */}
            <Col md={3} className="mb-3">
              <Form.Group className="mb-3">
                <Form.Label>Module</Form.Label>
                <Form.Select
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                >
                  <option value="">All Modules</option>
                  {moduleOptions && (moduleOptions.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  )))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Document Type Filter */}
            <Col md={3} className="mb-3">
              <Form.Group className="mb-3">
                <Form.Label>Document Type</Form.Label>
                <Form.Select
                  value={selectedDocumentType}
                  onChange={(e) => setSelectedDocumentType(e.target.value)}
                >
                  <option value="">All Document Types</option>
                  {Object.keys(documentTypeToSubTypesMap) && (Object.keys(documentTypeToSubTypesMap).map((docType) => (
                    <option key={docType} value={docType}>
                      {docType}
                    </option>
                  )))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Document Sub Type Filter */}
            <Col md={3} className="mb-3">
              <Form.Group className="mb-3">
                <Form.Label>Document Sub Type</Form.Label>
                <Form.Select
                  value={selectedDocumentSubType}
                  onChange={(e) => setSelectedDocumentSubType(e.target.value)}
                  disabled={!documentTypeToSubTypesMap[selectedDocumentType]}
                >
                  <option value="">All Document Sub Types</option>
                  {selectedDocumentType && documentTypeToSubTypesMap[selectedDocumentType] && (documentTypeToSubTypesMap[selectedDocumentType].map((docSubType) => (
                    <option key={docSubType} value={docSubType}>
                      {docSubType}
                    </option>
                  )))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            {/* Location Filter */}
            <Col md={3} className="mb-3">
              <Form.Group className="mb-3">
                <Form.Label>Location</Form.Label>
                <Form.Select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                >
                  <option value="">All Locations</option>
                  {sourceOptions && (sourceOptions.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  )))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Frequency Filter */}
            <Col md={3} className="mb-3">
              <Form.Group className="mb-3">
                <Form.Label>Frequency</Form.Label>
                <Form.Select
                  value={selectedFrequency}
                  onChange={(e) => handleFrequencyChange(e.target.value)}
                >
                  <option value="">All Frequencies</option>
                  <option value="EVERY_FY">YEARLY</option>
                  <option value="CUSTOM">{currentUser.frequency}</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Period Filter */}
            <Col md={3} className="mb-3">
              <Form.Group className="mb-3">
                <Form.Label>Period</Form.Label>
                <Form.Select
                  value={selectedPeriod}
                  onChange={(e) => handlePeriodChangeFilter(e.target.value)}
                  disabled={disablePeriodSelection || !selectedFrequency}
                >
                  <option value="">Select Period</option>
                  {timePeriodOptions && (timePeriodOptions.map((tp) => (
                    <option key={tp.value} value={tp.value}>
                      {tp.label}
                    </option>
                  )))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Date Range Display (when period is selected) */}
          {filterFromDate && filterToDate && (
            <Row>
              <Col md={12}>
                <div className="alert alert-info">
                  <strong>Selected Period:</strong> {moment(filterFromDate).format("DD MMM YYYY")} to {moment(filterToDate).format("DD MMM YYYY")}
                </div>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <Button 
            variant="outline-danger" 
            size="sm" 
            className="ms-3"
            onClick={applyFilters}
          >
            <FontAwesomeIcon icon={faSync} className="me-1" /> Retry
          </Button>
        </div>
      )}

      {/* Edit Error Message */}
      {editError && (
        <div className="alert alert-danger" role="alert">
          {editError}
        </div>
      )}

      <div className="documents-container">
        {filteredDocuments.length > 0 ? (
          <Row>
            {filteredDocuments.map((document, index) => {
              return (
                <Col
                  md={4}
                  className="mb-4"
                  key={document.id}
                >
                  <Card className="document-card h-100">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <strong>Document ID: {document.id || "N/A"}</strong>
                      <div>
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-2"
                          title="Edit Document"
                          onClick={() => openEditModal(document)}
                          disabled={editLoading}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          title="Preview Document"
                          onClick={() => openPreviewModal(document)}
                        >
                          <FontAwesomeIcon icon={faFileInvoice} />
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          title="Ask Question"
                          onClick={() => navigateToReportingModule(document.id || document.uuid)}
                        >
                          <FontAwesomeIcon icon={faQuestionCircle} />
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="document-details">
                        <p>
                          <strong>File Name:</strong> {document.fileMetadata?.fileName || "N/A"}
                        </p>
                        {/* <p>
                          <strong>Hash:</strong> <span className="document-hash">{document.fileMetadata?.hash || "N/A"}</span>
                        </p> */}
                        <p>
                          <strong>Upload Date:</strong> {document.createdAt 
                            ? moment(document.createdAt).format("DD MMM YYYY") 
                            : "N/A"}
                        </p>
                        <p>
                          <strong>User ID:</strong> {document.createdById || "N/A"}
                        </p>
                        {/* Display additional filter-related info */}
                        {document.moduleName && (
                          <p>
                            <strong>Module:</strong> {document.moduleName}
                          </p>
                        )}
                        {document.documentType && (
                          <p>
                            <strong>Document Type:</strong> {document.documentType}
                          </p>
                        )}
                        {document.documentSubType && (
                          <p>
                            <strong>Document Sub Type:</strong> {document.documentSubType}
                          </p>
                        )}
                        {document.sourceId && (
                          <p>
                            <strong>Location:</strong> {sourceOptions.find(s => s.value === document.sourceId)?.label || "N/A"}
                          </p>
                        )}
                        {document.frequency && (
                          <p>
                            <strong>Frequency:</strong> {document.frequency === 'EVERY_FY' ? 'YEARLY' : currentUser.frequency}
                          </p>
                        )}
                        {document.fromDate && document.toDate && (
                          <p>
                            <strong>Period:</strong> {getPeriodValue(document.fromDate, document.toDate)}
                          </p>
                        )}
                      </div>
                    </Card.Body>
                    <Card.Footer className="text-end">
                      {document.fileMetadata?.url && (
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          title="Download Document"
                          href={document.fileMetadata.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FontAwesomeIcon icon={faDownload} /> Download
                        </Button>
                      )}
                      {!document.fileMetadata?.url && (
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          disabled
                          title="Download not available"
                        >
                          <FontAwesomeIcon icon={faDownload} /> Download
                        </Button>
                      )}
                    </Card.Footer>
                  </Card>
                </Col>
              );
            })}
          </Row>
        ) : parentLoading || loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading documents...</p>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="text-center p-5">
            <h4>No documents match your filters</h4>
            <p>Try adjusting your search term or filter criteria to find documents.</p>
            <Button 
              variant="outline-primary" 
              onClick={resetFilters}
              className="mt-2 me-3"
            >
              <FontAwesomeIcon icon={faSync} className="me-2" />
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="text-center p-5">
            <h4>No documents found</h4>
            <p>Get started by uploading your first document.</p>
            <Button 
              variant="primary" 
              onClick={navigateToUpload}
              className="mt-2"
            >
              <FontAwesomeIcon icon={faUpload} className="me-2" />
              Upload Document
            </Button>
          </div>
        )}

        {/* Loading Spinner for more items */}
        {(loading || parentLoading) && documents.length > 0 && (
          <div className="text-center p-3">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading more documents...</span>
            </Spinner>
          </div>
        )}
      </div>

      {/* Document Preview Modal */}
      <Modal show={showPreviewModal} onHide={closePreviewModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Document Preview: {previewDocument && (previewDocument.document_id || previewDocument.id || "N/A")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewDocument && (
            <div className="document-preview">
              {/* Document preview iframe */}
              <div className="document-preview-placeholder">
                {previewDocument.fileMetadata?.url ? (
                  <iframe
                    src={previewDocument.fileMetadata.url}
                    title="Document Preview"
                    width="100%"
                    height="500px"
                    className="document-preview-frame"
                  />
                ) : (
                  <div className="preview-not-available">
                    <p>Preview not available for this document.</p>
                  </div>
                )}
              </div>

              <div className="document-metadata mt-3">
                <h6>Document Details:</h6>
                <Row>
                  <Col md={6}>
                    <p>
                      <strong>Document ID:</strong> {previewDocument.id || "N/A"}
                    </p>
                    <p>
                      <strong>File Name:</strong> {previewDocument.fileMetadata?.fileName || "N/A"}
                    </p>
                    <p>
                      <strong>Upload Date:</strong>{" "}
                      {previewDocument.createdAt ? moment(previewDocument.createdAt).format("DD MMM YYYY") 
                        : "N/A"}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>User ID:</strong> {previewDocument.createdById || "N/A"}
                    </p>
                    <p>
                      <strong>Hash:</strong>{" "}
                      <span className="document-hash">{previewDocument.fileMetadata?.hash || "N/A"}</span>
                    </p>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePreviewModal}>
            Close
          </Button>
          {previewDocument && (
            <>
              <Button
                variant="primary"
                onClick={() => navigateToReportingModule(previewDocument.fileMetadata?.uuid)}
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="me-1" />
                Ask Question
              </Button>
              {previewDocument.fileMetadata?.url && (
                <Button 
                  variant="outline-secondary"
                  href={previewDocument.fileMetadata.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faDownload} className="me-1" />
                  Download
                </Button>
              )}
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Document Edit Modal */}
      <Modal show={showEditModal} onHide={closeEditModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit Document: {editDocument?.id || "N/A"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {editLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading document details...</p>
            </div>
          ) : editDocument ? (
            <div className="edit-document-container">
              {/* File Metadata Display */}
              {editDocument.fileMetadata && (
                <div className="file-metadata-section mb-4">
                  <h6>File Information</h6>
                  <div className="file-metadata-grid">
                    <Row>
                      <Col md={6}>
                        <div className="metadata-item">
                          <strong>File Name:</strong>
                          <span className="metadata-value">{editDocument.fileMetadata.fileName || 'N/A'}</span>
                        </div>
                        <div className="metadata-item">
                          <strong>File Size:</strong>
                          <span className="metadata-value">
                            {editDocument.fileMetadata.fileSize
                              ? `${(editDocument.fileMetadata.fileSize / 1024 / 1024).toFixed(2)} MB`
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="metadata-item">
                          <strong>Upload Date:</strong>
                          <span className="metadata-value">
                            {editDocument.createdAt
                              ? moment(editDocument.createdAt).format("DD MMM YYYY, HH:mm")
                              : 'N/A'}
                          </span>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="metadata-item">
                          <strong>File Hash:</strong>
                          <span className="metadata-value hash-text">{editDocument.fileMetadata.hash || 'N/A'}</span>
                        </div>
                        <div className="metadata-item">
                          <strong>Uploaded By:</strong>
                          <span className="metadata-value">{editDocument.createdById || 'N/A'}</span>
                        </div>
                        {editDocument.fileMetadata.url && (
                          <div className="metadata-item">
                            <strong>File URL:</strong>
                            <span className="metadata-value">
                              <a
                                href={editDocument.fileMetadata.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="file-url-link"
                              >
                                View File
                              </a>
                            </span>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>
                </div>
              )}

              {/* Document Form */}
              <DocumentForm
                editDocument={editDocument}
                frameworks={frameworks}
                sourceOptions={sourceOptions}
                financialYearOptions={financialYearOptions}
                onSave={handleDocumentSave}
                success={editSuccess}
                error={editError}
              />
            </div>
          ) : (
            <div className="text-center p-5">
              <p>No document data available for editing.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DocumentListingComponent;